import { callRequest } from 'services/callRequestManager';
import configs from 'configs';
import getLogger from 'utils/getLogger';

const logger = getLogger('sessionManagerService');

/**
 * Pings the session-manager to verify and renew the session TTL.
 * Called periodically while the user is active (typing, clicking) to prevent
 * premature session expiry during long form interactions.
 *
 * Uses PATCH /api/v1/sessions/requests/{requestCode}, which triggers
 * verifyAndUpdateSessionByRequestCode() and renews the Redis TTL. Note that the
 * GET on this path is read-only and does NOT renew the TTL.
 *
 * The empty JSON body is required, not incidental: Akamai answers 411 Length
 * Required for a PATCH that declares no message length, and browsers only add
 * Content-Length automatically for POST and PUT. Content-Length is a forbidden
 * header name, so fetch ignores any attempt to set it directly and sending a
 * body is the only way to make the browser emit it.
 */
export const pingSession = (requestCode: string): Promise<void> => {
  const { BASE_URL, ENDPOINTS } = configs.params.SESSION_MANAGER_SERVER;
  const url = `${BASE_URL}${ENDPOINTS.KEEPALIVE_BY_REQUEST}/${requestCode}`;

  return callRequest<void>({
    url,
    method: 'PATCH',
    body: '{}',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).catch((err) => {
    // Session keepalive errors are non-fatal for the current interaction: the
    // user is redirected to login naturally when the session finally expires.
    // Raised from warn to error because a silent warning hid a total keepalive
    // outage. Note getLogger only emits in dev builds or with FORCE_LOGS set;
    // production visibility comes from the RUM error on the failed request.
    logger.error('Session keepalive failed', err);
  });
};
